import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Footer from './Footer';

const UnifiedIdentityPost = () => {
  useEffect(() => {
    document.title = "The Only Checklist You'll Ever Need for a Unified Identity";

    let metaDesc = document.querySelector('meta[name="description"]');
    const originalDescription = metaDesc ? metaDesc.getAttribute('content') : '';
    if (metaDesc) {
      metaDesc.setAttribute('content', 'Consolidating and maintaining a unified identity in a fragmented and everchanging revolve is peace of mind \u2014 if nothing else.');
    }

    return () => {
      document.title = 'Alan was here.';
      if (metaDesc) {
        metaDesc.setAttribute('content', originalDescription);
      }
    };
  }, []);

  return (
    <div className="post-page" data-testid="unified-identity-post">
      <header className="post-header">
        <Link to="/blog" className="back-button" data-testid="post-back-button">
          <ArrowLeft size={20} />
          <span>Back to Banter</span>
        </Link>
      </header>

      <main className="post-main">
        <article className="post-article">
          <title data-testid="post-title">The Only Checklist You'll Ever Need for an Adequate Unified Identity</title>
          <p className="post-title-display">The Only Checklist You'll Ever Need for an Adequate Unified Identity</p>
          <p className="post-subtitle" data-testid="post-subtitle">
            Consolidating and maintaining a unified identity in a fragmented and everchanging revolve is peace of mind — if nothing else.
          </p>
          <figure className="post-figure">
            <img src="https://customer-assets.emergentagent.com/job_406c254c-0dbb-4785-b762-b2be521745e0/artifacts/0nh7o7zr_lion-king-nala.png" alt="Nala from The Lion King" className="post-image" />
            <figcaption className="post-figcaption">
              <a href="https://www.pngegg.com/en/png-zxfxn" className="outline-link" target="_blank" rel="noopener noreferrer">Attribution</a>
              <p className="post-caption">Joseph Smith—History 1:1–26</p>
              <p className="post-caption">"there shall be a record kept among you" (Doctrine and Covenants 21:1)</p>
              <div className="post-video-embed">
                <iframe
                  src="https://www.youtube.com/embed/YHgwxVCiMyI?start=62&si=-upx71j5to95YOvh"
                  title="Video"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
            </figcaption>
          </figure>
          <h2 className="post-h2">Defining the Purpose and Goal of an Identity</h2>
          <div className="post-body">
            <p>Over the past decade I have neglected my health, wealth, and wellbeing. As a result, my reputation with friends, my credit, and my finances are in tatters. My mom once told me a long time ago that reputation takes time to curate and mature, and can be can easily be tarnished and destroyed with the smallest mistake.</p>
            <p>In order to recover and live in an appropriate sociable environment, I have to take a technical approach to <a href="https://en.wikipedia.org/wiki/Homo_erectus" className="outline-link" target="_blank" rel="noopener noreferrer">rectifying</a> this problem, such that I can pursue other avenues of interest. This isn't to say that burning bridges with collection agencies and friends isn't fun (someone once told me that 95% of people we encounter we'll never see again), but as my observation of existence expands, I'm naturally forced to narrow the scope of my trajectory such that what I do in my daily life meets certain constraints within a 16 hour waking window.</p>
            <p>Time is money, so it makes sense to run things in parallel. While I'm losing weight and getting shredded, I am taking a peek under the hood of my being.</p>
            <p>My identity, both online and off is straight shatter. With a geographic topology that is disparate, and interaction as well as communication being subject to constant misinterpretation, it's hard to keep a straight line of sight.</p>
            <p>It's on me then, and to keep myself whole in a world full of austerity and uncertainty, to consolidate and maintain a unified identity and portfolio that can be accessed with the swipe of a finger.</p>
            <p>Probably important to also note that this isn't ego, greed, or narcissism either. Many people work in government and private institutions to facilitate the many components that comprise identities to begin with.</p>
            <p>As such, taking the time to apply focus to such matters may be interpreted as form of bestowing credence to the pride that the people working at such institutions possess. A good deed no doubt, and requires only a bit of time.</p>
          </div>

          <nav className="post-outline" data-testid="post-outline">
            <ol className="outline-l1">
              <li>
                <span className="outline-heading">Types of Identification</span>
                <ol className="outline-l2">
                  <li>Birth Certificate</li>
                  <li>
                    <span>State ID</span>
                    <ol className="outline-l3">
                      <li>Driver's License</li>
                      <li>State ID</li>
                    </ol>
                  </li>
                  <li>
                    <span>Federal ID</span>
                    <ol className="outline-l3">
                      <li>Social Security</li>
                      <li>Passport</li>
                    </ol>
                  </li>
                </ol>
              </li>
              <li>
                <span className="outline-heading">Debts</span>
                <ol className="outline-l2">
                  <li>
                    <span>Taxes</span>
                    <ol className="outline-l3">
                      <li>Local</li>
                      <li>State</li>
                      <li>Federal</li>
                    </ol>
                  </li>
                  <li>
                    <span>Personal</span>
                    <ol className="outline-l3">
                      <li>Family</li>
                      <li>Friends</li>
                    </ol>
                  </li>
                </ol>
              </li>
              <li>
                <span className="outline-heading">Credit</span>
                <ol className="outline-l2">
                  <li>Financial</li>
                  <li>Social</li>
                </ol>
              </li>
              <li>
                <span className="outline-heading">Digital Identity</span>
                <ol className="outline-l2">
                  <li>Communication</li>
                  <li>Tools</li>
                  <li>Portfolio</li>
                  <li>
                    <span>Apps</span>
                    <ol className="outline-l3">
                      <li className="outline-detail">
                        <span className="outline-item-title">Atlas Earth</span>
                        <p className="outline-item-desc">Since the heyday of humanity, people always wanted to own real estate. Not all people however, are well endowed, and therefore can buy virtual real estate here; directly from the top (where noone actually loves), a great virtual nook that is for piece of mind. Attributes may include a sense of pride, ownership, oneupsmanship, etc. My favorite is buying virtual parcels of land that rest on top of others people residences (in a standalone server of course) such that they are unable to buy the next closest parcel within the vicinity of their couch. This thereby forces the person to take a walk outside without having to go far; like profitable posterity disguised as a mission-critical journey.</p>
                        <p className="outline-item-desc">This <a href="https://youtu.be/A0F9lh8TiSM?si=YIN2ZcX5mC864u0m" className="outline-link" target="_blank" rel="noopener noreferrer">song</a> comes to for no reason in particular.</p>
                      </li>
                    </ol>
                  </li>
                </ol>
              </li>
            </ol>
          </nav>
        </article>
      </main>

      <Footer />

      <style jsx>{`
        .post-page {
          min-height: 100vh;
          background: #151312;
          padding: 24px;
        }

        .post-header {
          max-width: 800px;
          margin: 0 auto 40px;
        }

        .back-button {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: transparent;
          border: 1px solid rgba(182, 180, 189, 0.3);
          color: #998f8f;
          padding: 10px 16px;
          border-radius: 8px;
          font-family: 'Poppins', sans-serif;
          font-size: 14px;
          text-decoration: none;
          transition: all 0.2s ease;
        }

        .back-button:hover {
          border-color: #f46c38;
          color: #f46c38;
        }

        .post-main {
          max-width: 800px;
          margin: 0 auto;
        }

        .post-article {
          padding: 0;
        }

        .post-title-display {
          font-family: 'Poppins', sans-serif;
          font-size: clamp(28px, 5vw, 44px);
          font-weight: 700;
          color: #fff;
          line-height: 1.2;
          letter-spacing: -0.02em;
          margin: 0 0 24px 0;
        }

        .post-subtitle {
          font-family: 'Poppins', sans-serif;
          font-size: 18px;
          color: #998f8f;
          line-height: 1.7;
          margin: 0 0 32px 0;
        }

        .post-figure {
          margin: 0 0 36px 0;
        }

        .post-image {
          max-width: 100%;
          height: auto;
          border-radius: 12px;
          display: block;
        }

        .post-figcaption {
          margin-top: 12px;
        }

        .post-figcaption .outline-link {
          font-family: 'Poppins', sans-serif;
          font-size: 13px;
        }

        .post-caption {
          font-family: 'Poppins', sans-serif;
          font-size: 14px;
          color: #998f8f;
          line-height: 1.6;
          margin: 6px 0 0 0;
          font-style: italic;
        }

        .post-video-embed {
          position: relative;
          width: 100%;
          padding-bottom: 56.25%;
          margin-top: 16px;
          border-radius: 12px;
          overflow: hidden;
        }

        .post-video-embed iframe {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          border: none;
        }

        .post-h2 {
          font-family: 'Poppins', sans-serif;
          font-size: 24px;
          font-weight: 600;
          color: #fff;
          margin: 0 0 32px 0;
          line-height: 1.3;
        }

        .post-body {
          margin: 0 0 40px 0;
        }

        .post-body p {
          font-family: 'Poppins', sans-serif;
          font-size: 16px;
          color: #998f8f;
          line-height: 1.8;
          margin: 0 0 20px 0;
        }

        .post-body p:last-child {
          margin-bottom: 0;
        }

        .post-outline {
          border-top: 1px solid rgba(182, 180, 189, 0.1);
          padding-top: 32px;
        }

        .post-outline ol {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .outline-l1 {
          counter-reset: l1;
        }

        .outline-l1 > li {
          counter-increment: l1;
          margin-bottom: 24px;
        }

        .outline-l1 > li::before {
          content: counter(l1) ". ";
        }

        .outline-heading {
          font-family: 'Poppins', sans-serif;
          font-size: 20px;
          font-weight: 600;
          color: #fff;
        }

        .outline-l1 > li::before {
          font-family: 'Poppins', sans-serif;
          font-size: 20px;
          font-weight: 600;
          color: #f46c38;
        }

        .outline-l2 {
          counter-reset: l2;
          margin-top: 10px;
          padding-left: 24px;
        }

        .outline-l2 > li {
          counter-increment: l2;
          font-family: 'Poppins', sans-serif;
          font-size: 16px;
          color: #c4bfbf;
          margin-bottom: 8px;
          line-height: 1.6;
        }

        .outline-l2 > li::before {
          content: counter(l1) "." counter(l2) ". ";
          color: #998f8f;
          font-size: 15px;
        }

        .outline-l3 {
          counter-reset: l3;
          margin-top: 6px;
          padding-left: 24px;
        }

        .outline-l3 > li {
          counter-increment: l3;
          font-family: 'Poppins', sans-serif;
          font-size: 15px;
          color: #998f8f;
          margin-bottom: 4px;
          line-height: 1.6;
        }

        .outline-l3 > li::before {
          content: counter(l1) "." counter(l2) "." counter(l3) ". ";
          color: #6b6363;
          font-size: 14px;
        }

        .outline-detail {
          margin-bottom: 12px;
        }

        .outline-detail::before {
          vertical-align: top;
        }

        .outline-item-title {
          font-weight: 600;
          color: #c4bfbf;
        }

        .outline-item-desc {
          font-family: 'Poppins', sans-serif;
          font-size: 14px;
          color: #7a7272;
          line-height: 1.75;
          margin: 6px 0 0 0;
        }

        .outline-link {
          color: #f46c38;
          text-decoration: none;
          border-bottom: 1px solid transparent;
          transition: border-color 0.2s ease;
        }

        .outline-link:hover {
          border-bottom-color: #f46c38;
        }

        @media (max-width: 600px) {
          .post-page {
            padding: 16px;
          }

          .post-subtitle {
            font-size: 16px;
          }
        }
      `}</style>
    </div>
  );
};

export default UnifiedIdentityPost;
